import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  objectToString,
  stringToObject,
  groupValidation,
} from '../../utils/form-utils';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { dateIsEmpty, parseDecimalPlacesRule } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

export const getColumns = () => ['CostPerHire', 'Successful', 'Failed'];
export const getRows = () => ['scs1', 'scs2', 'scs3', 'scs4'];
export const getKeys = () => getRows().flatMap((r) => getColumns().map((c) => `${r}${c}`));

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export function CoreCostSCSInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const { data } = useFormData();

  useEffect(() => {
    if (getValues(question.name) === undefined) setValue(question.name, '');
  }, [getValues, question.name, setValue]);

  const aId = useId();
  const makeId = (s: string) => `${anchorId ?? aId}-${s}`;

  const initialValues = useMemo(
    () => stringToObject(getValues(question.name)),
    [getValues, question.name],
  );
  const [values, setValues] = useState(initialValues);
  const decimalPlaces = parseDecimalPlacesRule(question.rules?.decimal);
  const allowFloat = typeof decimalPlaces === 'number';

  const { onChange } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        // Fields optional: no "all cells must be filled" validator
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: [], // no computed totals
        }),
      ]),
  });

  const headerLabel = (col: string) => {
    if (col === 'CostPerHire') return 'Cost per Hire';
    if (col === 'Successful') return 'Successful Appointments';
    if (col === 'Failed') return 'Failed Campaigns';
    return col;
  };

  const labelForRow = (row: string) => row.toUpperCase(); // SCS1..SCS4

  const triggerChange = (updated: typeof values) => {
    const asString = objectToString(updated);
    onChange({ target: { name: question.name, value: dateIsEmpty(asString) ? '' : asString } });
  };

  const handleChange = (field: keyof typeof values) => (val: string) => {
    const updated = { ...values, [field]: val };
    setValues(updated);
    triggerChange(updated);
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <table className="govuk-table sticky-header-table">
        <style>{`
          .sticky-header-table .govuk-table__head .govuk-table__header {
            position: sticky;
            top: 0;
            background: #ffffff;
            z-index: 2;
          }
        `}</style>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            {getColumns().map((col) => (
              <th key={col} scope="col" className="govuk-table__header">
                {headerLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {getRows().map((row) => (
            <tr key={row} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {labelForRow(row)}
              </th>
              {getColumns().map((col) => {
                const field = `${row}${col}` as keyof typeof values;
                return (
                  <td key={String(field)} className="govuk-table__cell">
                    <GradeInputColumn
                      testId={`${String(field)}-${labelForRow(row).toLowerCase()}`}
                      disabled={disabled}
                      allowFloat={allowFloat}
                      decimalPlaces={decimalPlaces}
                      anchorId={makeId(String(field))}
                      onChange={handleChange(field)}
                      value={values[field] ?? ''}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
